import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import '../styles/Earnings.css';

const Earnings = () => {
  const [activeTab, setActiveTab] = useState('request');
  const [loading, setLoading] = useState(true);
  const [earningsSummary, setEarningsSummary] = useState(null);
  const [earningsHistory, setEarningsHistory] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [pagination, setPagination] = useState({
    earnings: { currentPage: 1, totalPages: 1, hasNext: false, hasPrev: false },
    payouts: { currentPage: 1, totalPages: 1, hasNext: false, hasPrev: false }
  });

  // Payout request form state
  const [payoutForm, setPayoutForm] = useState({
    payoutMethod: '',
    amount: '',
    paymentDetails: ''
  });
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutError, setPayoutError] = useState('');
  const [payoutSuccess, setPayoutSuccess] = useState('');

  const payoutMethods = [
    { value: '', label: 'Select payout method' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'bank', label: 'Bank Transfer' },
    { value: 'upi', label: 'UPI' },
    { value: 'crypto', label: 'Cryptocurrency' }
  ];

  useEffect(() => {
    fetchEarningsSummary();
  }, []);

  useEffect(() => {
    if (activeTab === 'earnings') {
      fetchEarningsHistory();
    } else if (activeTab === 'payouts') {
      fetchPayoutHistory();
    }
  }, [activeTab]);

  const fetchEarningsSummary = async () => {
    try {
      const result = await apiService.payout.getSummary();
      if (result.success) {
        setEarningsSummary(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch earnings summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEarningsHistory = async (page = 1) => {
    try {
      const result = await apiService.payout.getEarningsHistory(page, 10);
      if (result.success) {
        setEarningsHistory(result.data.transactions);
        setPagination(prev => ({
          ...prev,
          earnings: result.data.pagination
        }));
      }
    } catch (error) {
      console.error('Failed to fetch earnings history:', error);
      setEarningsHistory([]);
    }
  };

  const fetchPayoutHistory = async (page = 1) => {
    try {
      const result = await apiService.payout.getPayoutHistory(page, 10);
      if (result.success) {
        setPayoutHistory(result.data.payouts);
        setPagination(prev => ({
          ...prev,
          payouts: result.data.pagination
        }));
      }
    } catch (error) {
      console.error('Failed to fetch payout history:', error);
      setPayoutHistory([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPayoutForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (payoutError) setPayoutError('');
    if (payoutSuccess) setPayoutSuccess('');
  };

  const handlePayoutSubmit = async (e) => {
    e.preventDefault();
    setPayoutLoading(true);
    setPayoutError('');
    setPayoutSuccess('');

    try {
      const result = await apiService.payout.submitRequest({
        payoutMethod: payoutForm.payoutMethod,
        amount: parseFloat(payoutForm.amount),
        paymentDetails: payoutForm.paymentDetails
      });
      
      if (result.success) {
        setPayoutSuccess(result.message);
        
        // Reset form
        setPayoutForm({
          payoutMethod: '',
          amount: '',
          paymentDetails: ''
        });
        
        // Refresh data
        await fetchEarningsSummary();
        if (activeTab === 'payouts') {
          await fetchPayoutHistory();
        }
      }
    } catch (err) {
      setPayoutError(err.message || 'Failed to submit payout request');
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleCancelPayout = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this payout request?')) {
      return;
    }

    try {
      const result = await apiService.payout.cancelRequest(requestId);
      if (result.success) {
        await fetchPayoutHistory();
        await fetchEarningsSummary();
      }
    } catch (error) {
      console.error('Failed to cancel payout:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      processing: 'status-processing',
      completed: 'status-completed',
      rejected: 'status-rejected',
      cancelled: 'status-cancelled'
    };
    
    return (
      <span className={`status-badge ${statusClasses[status] || 'status-pending'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="earnings-content">
        <div className="loading-container">
          <div className="loading-spinner-large"></div>
          <p>Loading earnings data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="earnings-content">
      {/* Earnings Summary Cards */}
      {earningsSummary && (
        <div className="earnings-summary">
          <div className="summary-cards">
            <div className="summary-card total-earned">
              <div className="summary-value">
                {apiService.utils.formatCurrency(earningsSummary.totalEarned)}
              </div>
              <div className="summary-label">Total Earned</div>
            </div>
            <div className="summary-card available-balance">
              <div className="summary-value">
                {apiService.utils.formatCurrency(earningsSummary.availableBalance)}
              </div>
              <div className="summary-label">Available Balance</div>
            </div>
            <div className="summary-card pending-amount">
              <div className="summary-value">
                {apiService.utils.formatCurrency(earningsSummary.pendingAmount)}
              </div>
              <div className="summary-label">Pending Payouts</div>
            </div>
            <div className="summary-card total-paid">
              <div className="summary-value">
                {apiService.utils.formatCurrency(earningsSummary.totalPaid)}
              </div>
              <div className="summary-label">Total Paid</div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="earnings-tabs">
        <button 
          className={`tab-btn ${activeTab === 'request' ? 'active' : ''}`}
          onClick={() => setActiveTab('request')}
        >
          💰 Request Payout
        </button>
        <button 
          className={`tab-btn ${activeTab === 'earnings' ? 'active' : ''}`}
          onClick={() => setActiveTab('earnings')}
        >
          📈 Earnings History
        </button>
        <button 
          className={`tab-btn ${activeTab === 'payouts' ? 'active' : ''}`}
          onClick={() => setActiveTab('payouts')}
        >
          🏦 Payout History
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'request' && (
          <div className="payout-request-section">
            <div className="payout-request-card">
              <h2 className="section-title">Request Payout</h2>
              
              {payoutError && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {payoutError}
                </div>
              )}

              {payoutSuccess && (
                <div className="success-message">
                  <span className="success-icon">✅</span>
                  {payoutSuccess}
                </div>
              )}

              <form onSubmit={handlePayoutSubmit} className="payout-form">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">PAYOUT METHOD</label>
                    <select
                      name="payoutMethod"
                      value={payoutForm.payoutMethod}
                      onChange={handleInputChange}
                      className="payout-select"
                      required
                      disabled={payoutLoading}
                    >
                      {payoutMethods.map(method => (
                        <option key={method.value} value={method.value}>
                          {method.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">AMOUNT ($)</label>
                    <input
                      type="number"
                      name="amount"
                      value={payoutForm.amount}
                      onChange={handleInputChange}
                      placeholder="Enter amount"
                      className="payout-input"
                      min="100"
                      max={earningsSummary?.availableBalance || 50000}
                      step="0.01"
                      required
                      disabled={payoutLoading}
                    />
                    <small className="form-help">
                      Available: {apiService.utils.formatCurrency(earningsSummary?.availableBalance || 0)}
                    </small>
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">PAYMENT DETAILS</label>
                  <textarea
                    name="paymentDetails"
                    value={payoutForm.paymentDetails}
                    onChange={handleInputChange}
                    placeholder="Enter PayPal email, UPI ID, or bank account details..."
                    className="payout-textarea"
                    rows="4"
                    required
                    disabled={payoutLoading}
                  />
                </div>
                
                <button type="submit" className="submit-payout-btn" disabled={payoutLoading}>
                  {payoutLoading ? (
                    <>
                      <span className="loading-spinner"></span>
                      Submitting Request...
                    </>
                  ) : (
                    'Submit Payout Request'
                  )}
                </button>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'earnings' && (
          <div className="earnings-history-section">
            <h2 className="section-title">Earnings History</h2>
            
            {earningsHistory.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📈</div>
                <p>No earnings yet. Start referring to earn commissions!</p>
              </div>
            ) : (
              <div className="transactions-table">
                <div className="table-header">
                  <div className="table-cell">Date</div>
                  <div className="table-cell">Description</div>
                  <div className="table-cell">Amount</div>
                  <div className="table-cell">Status</div>
                </div>
                
                {earningsHistory.map((transaction, index) => (
                  <div key={index} className="table-row">
                    <div className="table-cell" data-label="Date">
                      {apiService.utils.formatDate(transaction.date)}
                    </div>
                    <div className="table-cell" data-label="Description">
                      <div className="transaction-desc">
                        <span className="desc-title">{transaction.description}</span>
                        <span className="desc-subtitle">{transaction.referred_email}</span>
                      </div>
                    </div>
                    <div className="table-cell" data-label="Amount">
                      <span className="amount-positive">
                        +{apiService.utils.formatCurrency(transaction.amount)}
                      </span>
                    </div>
                    <div className="table-cell" data-label="Status">
                      {getStatusBadge(transaction.status)}
                    </div>
                  </div>
                ))}
                
                {/* Pagination for earnings */}
                {pagination.earnings.totalPages > 1 && (
                  <div className="pagination">
                    <button 
                      onClick={() => fetchEarningsHistory(pagination.earnings.currentPage - 1)}
                      disabled={!pagination.earnings.hasPrev}
                      className="pagination-btn"
                    >
                      ← Previous
                    </button>
                    <span className="pagination-info">
                      Page {pagination.earnings.currentPage} of {pagination.earnings.totalPages}
                    </span>
                    <button 
                      onClick={() => fetchEarningsHistory(pagination.earnings.currentPage + 1)}
                      disabled={!pagination.earnings.hasNext}
                      className="pagination-btn"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'payouts' && (
          <div className="payout-history-section">
            <h2 className="section-title">Payout History</h2>
            
            {payoutHistory.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🏦</div>
                <p>No payout requests yet. Submit your first payout request!</p>
              </div>
            ) : (
              <div className="transactions-table">
                <div className="table-header">
                  <div className="table-cell">Request ID</div>
                  <div className="table-cell">Method</div>
                  <div className="table-cell">Amount</div>
                  <div className="table-cell">Status</div>
                  <div className="table-cell">Date</div>
                  <div className="table-cell">Actions</div>
                </div>
                
                {payoutHistory.map((payout) => (
                  <div key={payout.id} className="table-row">
                    <div className="table-cell" data-label="Request ID">
                      <span className="request-id">{payout.request_id}</span>
                    </div>
                    <div className="table-cell" data-label="Method">
                      <span className="payout-method">
                        {payout.payout_method.toUpperCase()}
                      </span>
                    </div>
                    <div className="table-cell" data-label="Amount">
                      <span className="amount-negative">
                        -{apiService.utils.formatCurrency(payout.amount)}
                      </span>
                    </div>
                    <div className="table-cell" data-label="Status">
                      {getStatusBadge(payout.status)}
                    </div>
                    <div className="table-cell" data-label="Date">
                      {apiService.utils.formatDate(payout.created_at)}
                    </div>
                    <div className="table-cell" data-label="Actions">
                      {payout.status === 'pending' ? (
                        <button 
                          onClick={() => handleCancelPayout(payout.request_id)}
                          className="cancel-btn"
                        >
                          Cancel
                        </button>
                      ) : (
                        <span className="no-action">-</span>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Pagination for payouts */}
                {pagination.payouts.totalPages > 1 && (
                  <div className="pagination">
                    <button 
                      onClick={() => fetchPayoutHistory(pagination.payouts.currentPage - 1)}
                      disabled={!pagination.payouts.hasPrev}
                      className="pagination-btn"
                    >
                      ← Previous
                    </button>
                    <span className="pagination-info">
                      Page {pagination.payouts.currentPage} of {pagination.payouts.totalPages}
                    </span>
                    <button 
                      onClick={() => fetchPayoutHistory(pagination.payouts.currentPage + 1)}
                      disabled={!pagination.payouts.hasNext}
                      className="pagination-btn"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Earnings;